import { Component } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseDashboard } from '@memberjunction/ng-shared';
import type { ResourceData } from '@memberjunction/core-entities';
import {
    AGU_APP_SURFACE_CONFIG, AGURenderService, AGUSurfaceReadyService, BaseAGUAppSurfaceComponent, DecodeLocationParams,
    DecodeLocationParamsFromSearch, type AGUAppLocation, type UserActivityPageDescriptor,
} from 'agu_app_shell';
import { ExplorerOEPHostAdapter, OEP_SURFACE_CONFIG } from './host/oep-host.adapter';
import { OEPContextService, OEPNoSeasonError } from './state/oep-context.service';
import { OEPDataService } from './data/oep-data.service';
import { OEPFilesService } from './data/oep-files.service';
import { OEPActionsService } from './data/oep-actions.service';
import { OEPAt, OEPLandingFor, ParseOEPLocation, type OEPLocation } from './state/oep-location';

export type OEPExitKey = 'return' | 'sign-out';
export interface OEPExitAction { Key: OEPExitKey; Label: string; Variant: 'primary' | 'outline' }

/**
 * The OEP platform, hosted in MJExplorer as the FeedbackPlatform app's dashboard.
 * Driver class name is kept: metadata Dashboard B1A5F100-…0081 and activity tracking map it
 * (packages/AGUAppShell/src/lib/activity/user-activity-event.ts:86).
 *
 * Immersive lifecycle, visibility and location subscription come from
 * BaseAGUAppSurfaceComponent; this owns the readiness gate, the one context load, and the
 * deep-link landing. Views load their own data after the gate.
 */
@Component({
    standalone: false,
    selector: 'mj-oep-surface',
    templateUrl: './oep-surface.component.html',
    styleUrls: ['./oep-surface.component.scss'],
    // Specificity for the global token layer (styles.scss `oep-token-layer('mj-oep-surface')`), RA/FP precedent.
    host: { class: 'oep-scope' },
    /**
     * Per-surface providers, on the COMPONENT rather than OEPSurfaceModule. Every Explorer surface
     * module is eagerly imported into AppModule, so NgModule providers all land in the ONE root
     * injector: a module-level `AGU_APP_SURFACE_CONFIG` here would collide with RaSurfaceModule's
     * (last import wins — the OEP adapter would measure `mj-agu-surface` and toggle RA's body
     * class), and a root AGURenderService would repaint RA's views on every OEP notify. Scoping
     * them to this element injector gives the surface and every view under it (inject() walks the
     * element tree) its own adapter, config, repaint boundary and per-user context.
     */
    providers: [
        { provide: AGU_APP_SURFACE_CONFIG, useValue: OEP_SURFACE_CONFIG },
        ExplorerOEPHostAdapter,
        AGURenderService,
        OEPContextService,
        OEPDataService,
        OEPFilesService,
        OEPActionsService,
    ],
})
@RegisterClass(BaseDashboard, 'FeedbackPlatformAppSurface')
export class OEPSurfaceComponent extends BaseAGUAppSurfaceComponent<AGUAppLocation> {
    override location: AGUAppLocation = OEPAt('home');
    Ready = false;
    LoadError: string | null = null;
    NoSeason = false;
    NotificationsOpen = false;
    EmailsOpen = false;
    protected override readonly Adapter: ExplorerOEPHostAdapter;
    /**
     * The place the user asked for (deep link, reload), held until the context has loaded and the
     * allow-list can judge it. Captured in the constructor because the MJ shell replaces the URL
     * with the bare `/app/OEP` (router pushState, query dropped) while setup and the
     * context load are still in flight — reading `location.search` after them found the bare URL and
     * landed Home on every deployed-stage load (3 of 3)
     * (stage evidence: testing_results/2026-09-30-oep-v2-stage-deployed/s6-D2-deep-link-lost-root-cause.txt).
     * AGUUrlSyncedHostAdapter now keeps that shell navigation off the address bar (skipLocationChange),
     * so the URL survives too; capturing here stays the order-independent read.
     * A framework 'deeplink' delivery that arrives before load updates it.
     */
    private requested: OEPLocation | null;

    constructor(
        readonly Host: ExplorerOEPHostAdapter,
        readonly Context: OEPContextService,
        private readonly ready: AGUSurfaceReadyService,
        private readonly render: AGURenderService,
    ) {
        super();
        this.Adapter = Host;
        this.requested = ParseOEPLocation(DecodeLocationParamsFromSearch(this.Document.location.search));
        // In the constructor, not ngOnInit: BaseDashboard.ngOnInit kicks Initialize, which can
        // finish before a later hook would run (RA precedent).
        this.render.Attach(this.Cdr);
    }

    override ngOnDestroy(): void {
        this.render.Detach(this.Cdr);
        super.ngOnDestroy();
    }

    /** The header's notifications bell: a stable connected element for the emails dialog to return focus to. */
    readonly BellElement = (): HTMLElement | null => this.Document.querySelector<HTMLElement>('.oep-band__bell');

    /** The current place, narrowed; anything unparseable renders Home. */
    get View(): OEPLocation {
        return ParseOEPLocation(this.location) ?? OEPAt('home');
    }

    protected override DescribeLocation(l: AGUAppLocation): UserActivityPageDescriptor {
        return { Page: l.View, RecordID: l.RecordID ?? null };
    }

    async GetResourceDisplayName(_d: ResourceData): Promise<string> {
        return 'Outstanding Emerging Presenters';
    }

    protected initDashboard(): void {
        void this.Initialize();
    }

    protected loadData(): void {
        // Views load their own data after the readiness gate.
    }

    /**
     * Browser Back/Forward and URL edits: same allow-list as the initial deep link (OEPLandingFor).
     * Before the context has loaded the role-derived nav is empty, so judging now would send an
     * allowed view (e.g. Your reviews) Home; the request is held for Initialize instead.
     */
    protected override OnQueryParamsChanged(params: Record<string, string>, _source: 'popstate' | 'deeplink'): void {
        const loc = ParseOEPLocation(DecodeLocationParams(params));
        if (!loc) return;
        if (!this.Ready) {
            this.requested = loc;
            return;
        }
        this.Host.ResetTo(OEPLandingFor(loc, this.Context.Nav));
    }

    /**
     * Ways out of the header-less states (error, between seasons, no role). The surface is
     * immersive there too, so a user who holds another app must be offered the way back — the same
     * CanReturnToHost/ReturnToHost the header's account menu uses — beside Sign out.
     */
    get ExitActions(): OEPExitAction[] {
        const signOut: OEPExitAction = { Key: 'sign-out', Label: 'Sign out', Variant: 'outline' };
        return this.Host.CanReturnToHost ? [{ Key: 'return', Label: 'Back to MJ Explorer', Variant: 'primary' }, signOut] : [{ ...signOut, Variant: 'primary' }];
    }

    RunExit(key: OEPExitKey): void {
        if (key === 'return') this.Host.ReturnToHost();
        else void this.Host.SignOut();
    }

    /** Readiness → context → land on the deep link when the user may see it, else Home. Fails closed to an explicit state. */
    private async Initialize(): Promise<void> {
        try {
            await this.ready.WaitForSetup();
            await this.Context.Load();
            this.Ready = true;
            this.Host.ResetTo(OEPLandingFor(this.requested, this.Context.Nav));
            this.requested = null;
        } catch (err) {
            this.NoSeason = err instanceof OEPNoSeasonError;
            this.LoadError = err instanceof Error ? err.message : String(err);
        } finally {
            this.render.Refresh();
        }
    }
}

/** Imported for side effects from the module so the @RegisterClass registration is not tree-shaken. */
export function LoadOEPSurface(): void {
    // no-op; the decorator runs on import
}
